import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, auth, inputCls, Field, Modal, PrimaryBtn, errMsg } from '../finance/shared';
import PremiumDatePicker from '../../../components/ui/PremiumDatePicker';

const TITLES = {
  payment: 'تسجيل دفعة اشتراك', renew: 'تجديد الاشتراك', extend: 'تمديد مجاني', cancel: 'إلغاء الاشتراك',
};
const PLAYER_PAYMENT_METHODS = { cash: 'كاش', network: 'شبكة', transfer: 'تحويل', electronic: 'إلكتروني', other: 'أخرى' };
const DURATION_UNIT_LABELS = { day: 'يوم', week: 'أسبوع', month: 'شهر', year: 'سنة' };
const CUSTOM_PACKAGE_VALUE = '__custom__';
const addDaysIso = (dateStr, days) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const packageDurationDays = (pkg) => (Number(pkg.duration_value) || 1) * ({ day: 1, week: 7, month: 30, year: 365 }[pkg.duration_unit] || 30);
const computeFinalAmount = (basePrice, discountType, discountValue) => {
  const base = Number(basePrice) || 0;
  const dv = Number(discountValue) || 0;
  if (!discountType || !dv) return base;
  const final = discountType === 'percent' ? base * (1 - Math.min(dv, 100) / 100) : Math.max(0, base - dv);
  return Math.round(final * 100) / 100;
};

export default function SubscriptionActionModal({ action, onClose, onDone }) {
  const [form, setForm] = useState({ amount: '', payment_method: 'cash', notes: '', subscription_start: '', subscription_end: '', subscription_amount: '', subscription_package: '', subscription_package_id: '', subscription_discount_type: '', subscription_discount_value: '', subscription_discount_reason: '', subscription_payment_method: '', subscription_payment_reference: '', new_end: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [idemKey] = useState(() => `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [packages, setPackages] = useState([]);
  const [basePrice, setBasePrice] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);

  useEffect(() => {
    if (action?.type === 'renew') {
      setForm((f) => ({ ...f, subscription_start: action.player.subscription_start ? String(action.player.subscription_start).slice(0, 10) : '', subscription_end: action.player.subscription_end ? String(action.player.subscription_end).slice(0, 10) : '', subscription_amount: action.player.subscription_amount ?? '', subscription_package: action.player.subscription_package || '', subscription_package_id: action.player.subscription_package_id || '' }));
      setBasePrice(action.player.subscription_amount ?? null);
      axios.get(`${API}/subscription-packages`, auth()).then((res) => setPackages(Array.isArray(res.data) ? res.data : [])).catch(() => setPackages([]));
    }
  }, [action]);

  if (!action) return null;
  const { type, player } = action;

  const onPackageSelect = (packageId) => {
    if (packageId === CUSTOM_PACKAGE_VALUE || !packageId) { setForm((f) => ({ ...f, subscription_package_id: '' })); return; }
    const pkg = packages.find((x) => x.id === packageId);
    if (!pkg) return;
    setBasePrice(pkg.price);
    setForm((f) => ({ ...f, subscription_package_id: pkg.id, subscription_package: pkg.name, subscription_amount: computeFinalAmount(pkg.price, f.subscription_discount_type, f.subscription_discount_value), subscription_end: addDaysIso(f.subscription_start, packageDurationDays(pkg)) }));
  };

  const onDiscountChange = (patch) => {
    setForm((f) => {
      const next = { ...f, ...patch };
      if (basePrice != null) next.subscription_amount = computeFinalAmount(basePrice, next.subscription_discount_type, next.subscription_discount_value);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (type === 'payment' && (!form.amount || Number(form.amount) <= 0)) { setError('أدخل مبلغاً صحيحاً أكبر من صفر'); return; }
    if (type === 'renew' && (!form.subscription_start || !form.subscription_end || form.subscription_end < form.subscription_start)) { setError('تحقق من تاريخي البداية والنهاية'); return; }
    if (type === 'extend' && (!form.new_end || !form.reason?.trim())) { setError('تاريخ النهاية الجديد والسبب مطلوبان'); return; }
    if (type === 'cancel' && !form.reason?.trim()) { setError('سبب الإلغاء مطلوب'); return; }

    setBusy(true);
    try {
      if (type === 'payment') {
        await axios.post(`${API}/players/${player.id}/subscription/payment`, { amount: Number(form.amount), payment_method: form.payment_method, notes: form.notes || null, idempotency_key: idemKey }, auth());
      } else if (type === 'renew') {
        await axios.post(`${API}/players/${player.id}/subscription/renew`, {
          subscription_start: form.subscription_start, subscription_end: form.subscription_end,
          subscription_amount: form.subscription_amount !== '' ? Number(form.subscription_amount) : null,
          subscription_package: form.subscription_package || null, subscription_package_id: form.subscription_package_id || null,
          subscription_discount_type: form.subscription_discount_type || null,
          subscription_discount_value: form.subscription_discount_value !== '' ? Number(form.subscription_discount_value) : null,
          subscription_discount_reason: form.subscription_discount_reason || null,
          subscription_payment_method: form.subscription_payment_method || null,
          subscription_payment_reference: form.subscription_payment_reference || null,
        }, auth());
        if (receiptFile) {
          const fd = new FormData(); fd.append('file', receiptFile);
          await axios.post(`${API}/players/${player.id}/subscription/receipt`, fd, auth());
        }
      } else if (type === 'extend') {
        await axios.post(`${API}/players/${player.id}/subscription/extend-free`, { new_end: form.new_end, reason: form.reason }, auth());
      } else if (type === 'cancel') {
        await axios.post(`${API}/players/${player.id}/subscription/cancel`, { reason: form.reason }, auth());
      }
      onDone();
    } catch (err) {
      setError(errMsg(err, 'فشلت العملية'));
      setBusy(false);
    }
  };

  return (
    <Modal open title={`${TITLES[type]} — ${player.name}`} onClose={onClose}>
      {error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="sub-action-error">{error}</div>}
      <form onSubmit={submit} className="space-y-3">
        {type === 'payment' && (
          <>
            <Field label="المبلغ (ر.س)" required><input type="number" min="0.01" step="0.01" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" /></Field>
            <Field label="طريقة الدفع">
              <select className={inputCls} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="payment-method-select">
                <option value="cash">نقدي</option><option value="bank_transfer">تحويل بنكي</option><option value="credit_card">بطاقة ائتمان</option><option value="check">شيك</option>
              </select>
            </Field>
            <Field label="ملاحظات"><input className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </>
        )}
        {type === 'renew' && (
          <>
            <Field label="الباقة">
              <select className={inputCls} value={form.subscription_package_id || CUSTOM_PACKAGE_VALUE} onChange={(e) => onPackageSelect(e.target.value)} data-testid="renew-package-select">
                <option value={CUSTOM_PACKAGE_VALUE}>اشتراك مخصص (إدخال يدوي)</option>
                {packages.map((pk) => <option key={pk.id} value={pk.id}>{pk.name} — {pk.price} ر.س / {pk.duration_value} {DURATION_UNIT_LABELS[pk.duration_unit]}</option>)}
              </select>
            </Field>
            <Field label="بداية الدورة الجديدة" required><PremiumDatePicker value={form.subscription_start} onChange={(v) => setForm({ ...form, subscription_start: v })} testId="renew-start-input" /></Field>
            <Field label="نهاية الدورة الجديدة" required><PremiumDatePicker value={form.subscription_end} onChange={(v) => setForm({ ...form, subscription_end: v })} testId="renew-end-input" /></Field>
            <Field label="قيمة الاشتراك (ر.س)"><input type="number" min="0" className={inputCls} value={form.subscription_amount} onChange={(e) => setForm({ ...form, subscription_amount: e.target.value })} data-testid="renew-amount-input" /></Field>
            <Field label="اسم/نوع الباقة"><input className={inputCls} value={form.subscription_package} onChange={(e) => setForm({ ...form, subscription_package: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="نوع الخصم">
                <select className={inputCls} value={form.subscription_discount_type} onChange={(e) => onDiscountChange({ subscription_discount_type: e.target.value })} data-testid="renew-discount-type">
                  <option value="">بدون خصم</option><option value="amount">مبلغ ثابت</option><option value="percent">نسبة %</option>
                </select>
              </Field>
              <Field label="قيمة الخصم"><input type="number" min="0" className={inputCls} value={form.subscription_discount_value} onChange={(e) => onDiscountChange({ subscription_discount_value: e.target.value })} disabled={!form.subscription_discount_type} data-testid="renew-discount-value" /></Field>
            </div>
            <Field label="سبب الخصم (اختياري)"><input className={inputCls} value={form.subscription_discount_reason} onChange={(e) => setForm({ ...form, subscription_discount_reason: e.target.value })} disabled={!form.subscription_discount_type} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="طريقة الدفع">
                <select className={inputCls} value={form.subscription_payment_method} onChange={(e) => setForm({ ...form, subscription_payment_method: e.target.value })} data-testid="renew-payment-method">
                  <option value="">غير محدد</option>
                  {Object.entries(PLAYER_PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="مرجع الدفع (اختياري)"><input className={inputCls} value={form.subscription_payment_reference} onChange={(e) => setForm({ ...form, subscription_payment_reference: e.target.value })} /></Field>
            </div>
            <Field label="إيصال الدفع (اختياري)">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="text-xs" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} data-testid="renew-receipt-upload" />
            </Field>
          </>
        )}
        {type === 'extend' && (
          <>
            <Field label="تاريخ النهاية الجديد" required><PremiumDatePicker value={form.new_end} onChange={(v) => setForm({ ...form, new_end: v })} testId="extend-end-input" /></Field>
            <Field label="سبب التمديد (إلزامي)" required><input className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="extend-reason-input" /></Field>
          </>
        )}
        {type === 'cancel' && (
          <>
            <p className="text-sm text-slate-500">سيتم إلغاء اشتراك «{player.name}». هذا الإجراء يمكن التراجع عنه لاحقاً بتجديد جديد.</p>
            <Field label="سبب الإلغاء (إلزامي)" required><input className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cancel-reason-input" /></Field>
          </>
        )}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <PrimaryBtn type="submit" disabled={busy} data-testid="sub-action-submit">{busy ? 'جارٍ التنفيذ...' : 'تأكيد'}</PrimaryBtn>
        </div>
      </form>
    </Modal>
  );
}
